import { Robot } from '@/entities/Robot';
import { Enemy } from '@/entities/Enemy';
import { BehaviorContext } from '@/behaviors/BehaviorTypes';
import { ComponentType, COMPONENT_DEFS, MINE_CONFIG, EXCAVATOR_CONFIG, getEvolutionMultiplier } from '@/components/ComponentDefs';
import { EVOLUTION_MAP } from '@/config/ComponentEvolution';
import { fireCooldowns } from '@/behaviors/BehaviorCombat';
import { getComponentTextureKeyAtLevel } from '@/systems/ComponentIcons';
import { SoundManager } from '@/systems/SoundManager';
import { EventBus, GameEvents } from '@/events/EventBus';
import { GOD } from '@/config/GodMode';
import { MAP_DECORATIONS, damageDecor, removeDecor } from '@/map/MapData';
import { TILE_SIZE, DECOR_RESOURCE_YIELDS } from '@/config/GameConfig';

// ── Passive side-jumper push ──
// Evolution calibration (base → lvl1 → lvl2 → lvl3):
//   Push tiles:  2 → 3 → 4 → 5    (significantly more area control)
//   Cooldown:    3.0 → 2.5 → 2.0 → 1.5s  (faster cycle = more disruption)
//   Energy cost: 3 → 4 → 5 → 6    (forces recharge module pairing at high levels)

const JUMPER_PUSH_COOLDOWN_BY_LEVEL = [3.0, 2.5, 2.0, 1.5];
const JUMPER_PUSH_ENERGY_BY_LEVEL = [3, 4, 5, 6];

// Squeeze/stretch animation constants
const JUMPER_STRETCH_SCALE = 1.5;   // stretch factor on release (along push axis)
const JUMPER_STRETCH_PINCH = 0.65;  // perpendicular pinch during stretch
const JUMPER_STRETCH_MS = 60;       // stretch duration (fast snap)
const JUMPER_SQUEEZE_MIN = 0.6;     // most compressed scale (just after push)

// Track max cooldown per jumper for squeeze animation ratio
const jumperMaxCooldowns = new Map<string, number>();

/** Base excavator DPS (before evolution scaling) */
const EXCAVATOR_BASE_DPS = 8;

/** Clear all cached state for passives module */
export function resetPassivesCaches(): void {
  jumperMaxCooldowns.clear();
}

// ── Passive mine laying ──
// Robots with MINE_LAYER components drop mines on their current tile periodically.
// No behavior rules required — works like passive support.
export function passiveMineLayerTick(robot: Robot, ctx: BehaviorContext): void {
  if (!ctx.mineManager) return;
  const dt = ctx.delta / 1000;

  const mineModules = robot.components.filter(
    c => !c.broken && c.def.type === ComponentType.MINE_LAYER,
  );
  if (mineModules.length === 0) return;

  for (const m of mineModules) {
    // Unique cooldown per mine layer module (not shared)
    const cdKey = `${robot.id}_mine_${m.localX}_${m.localY}`;
    const remaining = (fireCooldowns.get(cdKey) ?? 0) - dt;
    if (remaining > 0) {
      fireCooldowns.set(cdKey, remaining);
      continue;
    }

    // Tile position
    const col = Math.floor(robot.x / TILE_SIZE);
    const row = Math.floor(robot.y / TILE_SIZE);

    // Evolved stats — use per-component tier (Pattern B)
    const evoLevel = m.evoLevel;
    const mineChain = EVOLUTION_MAP.get(ComponentType.MINE_LAYER);
    const mult = mineChain?.stages.find(s => s.level === evoLevel)?.statMultiplier ?? getEvolutionMultiplier(evoLevel);
    const stats = {
      damage: m.def.damage * mult,
      triggerRadius: m.def.weaponRange * mult,
      explosionRadius: (m.def.explosionRadius || 50) * mult,
      stunDuration: MINE_CONFIG.stunDuration * mult,
      pushbackTiles: MINE_CONFIG.pushbackTiles + Math.floor(evoLevel * 0.5),
    };

    const texKey = getComponentTextureKeyAtLevel(ComponentType.MINE_LAYER, evoLevel) ?? 'comp_mine_layer_0';
    const spawned = ctx.mineManager.spawn(robot.x, robot.y, col, row, stats, evoLevel, texKey);
    // Always set cooldown — don't waste energy retrying every frame when tile is full
    fireCooldowns.set(cdKey, (m.def.cooldown || MINE_CONFIG.layCooldown) * (1 - 0.1 * evoLevel));
    // Only consume energy if mine was actually placed
    if (spawned) {
      robot.consumeEnergy(MINE_CONFIG.energyPerMine, true);
    }
  }
}

// ── Passive side-jumper push ──
// Side jumpers detect enemies on the adjacent tile in the jumper's facing direction.
// On trigger: push enemy N tiles in that direction (into hazards = tactical kill).
// No damage — pure terrain control (water/magma/chasm = death, mud = slow).
//
// Animation: on release the spring stretches rapidly along push axis (eject),
// then slowly squeezes back during cooldown (kinetic energy recharge).
export function passiveJumperTick(robot: Robot, ctx: BehaviorContext): void {
  const dt = ctx.delta / 1000;
  const sideJumpers = robot.getSideJumpers();
  if (sideJumpers.length === 0) return;

  const jumperDef = COMPONENT_DEFS[ComponentType.JUMPER];

  for (const j of sideJumpers) {
    const evoLevel = j.evoLevel;
    const pushTiles = GOD.jumperPushTilesBase + evoLevel * GOD.jumperPushTilesPerLevel;
    const baseCd = jumperDef.cooldown || JUMPER_PUSH_COOLDOWN_BY_LEVEL[0];
    const maxCd = baseCd * (1 - 0.1 * evoLevel);
    const energyCost = JUMPER_PUSH_ENERGY_BY_LEVEL[evoLevel] ?? JUMPER_PUSH_ENERGY_BY_LEVEL[0];
    const cdKey = `${robot.id}_jumper_${j.localX}_${j.localY}`;
    const remaining = (fireCooldowns.get(cdKey) ?? 0) - dt;

    // ── Squeeze animation: compress → expand during cooldown ──
    if (remaining > 0) {
      fireCooldowns.set(cdKey, remaining);
      const storedMax = jumperMaxCooldowns.get(cdKey) ?? maxCd;
      const ratio = Math.min(remaining / storedMax, 1); // 1 = just fired, 0 = ready
      // Scale along action axis: compressed when ratio=1, normal when ratio=0
      const squeeze = JUMPER_SQUEEZE_MIN + (1 - JUMPER_SQUEEZE_MIN) * (1 - ratio);
      const bulge = 1 + (1 - squeeze) * 0.4; // slight perpendicular bulge when compressed
      applyJumperScale(j, squeeze, bulge);
      continue;
    }
    // Fully charged — reset to normal scale
    applyJumperScale(j, 1, 1);

    // Push direction: outward from robot center through the jumper component
    // Must account for robot flip + container rotation (same transform as getWeaponWorldPos)
    let dirX = robot.flipped ? -j.localX : j.localX;
    let dirY = j.localY;
    const rot = robot.container.rotation;
    if (Math.abs(rot) > 0.001) {
      const cos = Math.cos(rot);
      const sin = Math.sin(rot);
      const rx = dirX * cos - dirY * sin;
      const ry = dirX * sin + dirY * cos;
      dirX = rx;
      dirY = ry;
    }
    const dirMag = Math.sqrt(dirX * dirX + dirY * dirY);
    if (dirMag < 0.1) continue;
    const pushDirX = dirX / dirMag;
    const pushDirY = dirY / dirMag;

    // Detection check: jumper world pos + detectRange in push direction
    const wPos = robot.getWeaponWorldPos(j);
    const detectDist = TILE_SIZE * GOD.jumperDetectRange;
    const adjX = wPos.x + pushDirX * detectDist;
    const adjY = wPos.y + pushDirY * detectDist;
    const adjCol = Math.floor(adjX / TILE_SIZE);
    const adjRow = Math.floor(adjY / TILE_SIZE);

    // Find any alive enemy standing on the adjacent tile
    // Skip enemies already in knockback (prevents velocity stacking each frame)
    let target: Enemy | null = null;
    for (const e of ctx.enemies) {
      if (!e.alive || e.isKnockedBack) continue;
      const eCol = Math.floor(e.body.x / TILE_SIZE);
      const eRow = Math.floor(e.body.y / TILE_SIZE);
      if (eCol === adjCol && eRow === adjRow) {
        target = e;
        break;
      }
    }

    if (target) {
      if (!robot.consumeEnergy(energyCost, true)) continue;

      // Store start position for animation
      const startX = target.body.x;
      const startY = target.body.y;

      // Directional push (not along path)
      const endPos = target.pushInDirection(pushDirX, pushDirY, pushTiles);

      // ── Stretch animation: rapid spring release ──
      if (j.graphic && robot.scene) {
        const isH = Math.abs(j.localX) > Math.abs(j.localY);
        // Kill any existing tween on this graphic
        robot.scene.tweens.killTweensOf(j.graphic);
        // Snapshot base scales before tween
        const jc = j as any;
        if (jc._baseScaleX === undefined) {
          jc._baseScaleX = j.graphic.scaleX;
          jc._baseScaleY = j.graphic.scaleY;
        }
        const bx = jc._baseScaleX as number;
        const by = jc._baseScaleY as number;
        const stretchProps = isH
          ? { scaleX: bx * JUMPER_STRETCH_SCALE, scaleY: by * JUMPER_STRETCH_PINCH }
          : { scaleX: bx * JUMPER_STRETCH_PINCH, scaleY: by * JUMPER_STRETCH_SCALE };
        robot.scene.tweens.add({
          targets: j.graphic,
          ...stretchProps,
          duration: JUMPER_STRETCH_MS,
          ease: 'Back.easeOut',
          yoyo: false,
          onComplete: () => {
            // Snap to squeezed state — frame updates take over from here
            applyJumperScale(j, JUMPER_SQUEEZE_MIN, 1 + (1 - JUMPER_SQUEEZE_MIN) * 0.4);
          },
        });
      }

      // Visual + audio feedback
      SoundManager.play('jumpBoing');
      EventBus.emit(GameEvents.JUMPER_PUSH, {
        robot, comp: j, target,
        dirX: pushDirX, dirY: pushDirY,
        startX, startY,
        endX: endPos?.x ?? startX, endY: endPos?.y ?? startY,
      });

      jumperMaxCooldowns.set(cdKey, maxCd);
      fireCooldowns.set(cdKey, maxCd);
    }
  }
}

/** Apply scale to a jumper graphic along its dominant push axis.
 *  Multiplies the base scale (from createComponentVisual) — never replaces it. */
export function applyJumperScale(comp: { localX: number; localY: number; graphic?: any; flipX?: boolean; flipY?: boolean }, axisScale: number, perpScale: number): void {
  if (!comp.graphic) return;
  // Retrieve or snapshot base scales from createComponentGraphic
  const c = comp as any;
  if (c._baseScaleX === undefined) {
    c._baseScaleX = comp.graphic.scaleX;
    c._baseScaleY = comp.graphic.scaleY;
  }
  const bx = c._baseScaleX;
  const by = c._baseScaleY;
  const isH = Math.abs(comp.localX) > Math.abs(comp.localY);
  if (isH) {
    comp.graphic.setScale(bx * axisScale, by * perpScale);
  } else {
    comp.graphic.setScale(bx * perpScale, by * axisScale);
  }
}

// ── Passive excavator: mine resources from nearby harvestable decorations ──
// Excavator deals DPS to resource HP. When HP reaches 0, resource is collected.
export function passiveExcavatorTick(robot: Robot, ctx: BehaviorContext): void {
  if (robot.energySaveMode) return;  // Stop mining when energy is low
  const dt = ctx.delta / 1000;

  const excavators = robot.components.filter(
    c => !c.broken && c.def.type === ComponentType.EXCAVATOR,
  );
  if (excavators.length === 0) return;

  // Use best excavator (highest evo level) for mining
  const exc = excavators.reduce((best, c) => c.evoLevel > best.evoLevel ? c : best);

  // Evolution scaling
  const evoLevel = exc.evoLevel;
  const excChain = EVOLUTION_MAP.get(ComponentType.EXCAVATOR);
  const mult = excChain?.stages.find(s => s.level === evoLevel)?.statMultiplier ?? getEvolutionMultiplier(evoLevel);
  const harvestRange = exc.def.supportRange * mult;
  const yieldMult = EXCAVATOR_CONFIG.yieldMultBase + evoLevel * 0.25;
  const dps = EXCAVATOR_BASE_DPS * mult * GOD.excavatorDpsMult;

  // Find nearest harvestable decoration in range
  const rx = robot.x;
  const ry = robot.y;
  let bestDecor: typeof MAP_DECORATIONS[0] | null = null;
  let bestDistSq = harvestRange * harvestRange;

  for (let i = 0; i < MAP_DECORATIONS.length; i++) {
    const d = MAP_DECORATIONS[i];
    const yields = DECOR_RESOURCE_YIELDS[d.type];
    if (!yields) continue;

    const dx = d.col * TILE_SIZE + TILE_SIZE / 2 - rx;
    const dy = d.row * TILE_SIZE + TILE_SIZE / 2 - ry;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestDecor = d;
    }
  }

  if (!bestDecor) return; // nothing in range

  // Face toward the resource being mined
  robot.faceTargetX = bestDecor.col * TILE_SIZE + TILE_SIZE / 2;

  // Energy drain (continuous while mining)
  if (!robot.consumeEnergy(EXCAVATOR_CONFIG.energyPerHarvest * dt, true)) return;

  // Deal mining damage
  const damage = dps * dt;
  const remainingHp = damageDecor(bestDecor, damage);

  // Mining SFX (throttled — one tick every ~0.4s)
  robot._miningSfxTimer = (robot._miningSfxTimer ?? 0) - dt;
  if (robot._miningSfxTimer <= 0) {
    robot._miningSfxTimer = 0.4;
    SoundManager.play('mining');
  }

  // Emit mining event (for HP bar display)
  const maxHp = DECOR_RESOURCE_YIELDS[bestDecor.type]!.hp;
  EventBus.emit(GameEvents.DECOR_MINING, {
    col: bestDecor.col,
    row: bestDecor.row,
    hp: remainingHp,
    maxHp,
    robotX: robot.x,
    robotY: robot.y,
  });

  // Resource depleted → collect it
  if (remainingHp <= 0) {
    const yields = DECOR_RESOURCE_YIELDS[bestDecor.type]!;
    const coGain = Math.round(yields.co * yieldMult);
    const electroGain = Math.round(yields.electro * yieldMult);

    removeDecor(bestDecor);

    SoundManager.play('harvestComplete');

    EventBus.emit(GameEvents.DECOR_HARVESTED, {
      col: bestDecor.col,
      row: bestDecor.row,
      type: bestDecor.type,
      co: coGain,
      electro: electroGain,
      robotX: robot.x,
      robotY: robot.y,
    });
  }
}

/** Lock radar sweep toward detected enemies, or release to free sweep if none. */
export function updateSweepLock(robot: Robot, ctx: BehaviorContext): void {
  if (!robot.hasDirectionComponent || robot.sweepSpeed <= 0) {
    robot.sweepLockTarget = null;
    return;
  }

  // Gather alive enemies still in sweep memory
  const now = Date.now();
  const maxAge = Robot.SWEEP_MEMORY_DURATION * 1000;
  let sumX = 0, sumY = 0, count = 0;

  for (const [enemyId, ts] of robot.sweepMemory) {
    if (now - ts > maxAge) continue;
    // Find the enemy object to get current position
    const enemy = ctx.enemies.find(e => e.id === enemyId && e.alive);
    if (enemy) {
      sumX += enemy.body.x;
      sumY += enemy.body.y;
      count++;
    }
  }

  if (count > 0) {
    robot.sweepLockTarget = { x: sumX / count, y: sumY / count };
  } else {
    robot.sweepLockTarget = null;
  }
}
